import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ArrowRight, ArrowDown, Sparkles, Code2, BookOpen, Zap, Globe, Layers, Shield } from 'lucide-react';
import { useLang } from '../i18n/LangContext';
import { SectionEyebrow, SectionTitle, FadeIn, StaggerList, StaggerItem } from '../components/Animated';
import Counter from '../components/Counter';

const HERO_IMG = 'https://images.unsplash.com/photo-1622737133809-d95047b9e673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHwzZCUyMGFic3RyYWN0JTIwc2hhcGVzJTIwZGFya3xlbnwwfHx8fDE3ODIyMTQwMTN8MA&ixlib=rb-4.1.0&q=85';
const ERP_IMG = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NTYxODh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZHxlbnwwfHx8fDE3ODIyMTQwMTN8MA&ixlib=rb-4.1.0&q=85';
const WEB_IMG = 'https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const TECH = ['React', 'Next.js', 'FastAPI', 'TypeScript', 'PostgreSQL', 'MongoDB', 'Tailwind', 'Node.js', 'Python', 'Docker', 'AWS', 'Stripe'];

const HomePage = () => {
  const { t, isRTL } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div data-testid="home-page" className="relative">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid bg-grid-radial pointer-events-none" />
        <div className="absolute inset-0 noise pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div style={{ y: titleY }} className="lg:col-span-7 relative z-10">
            <FadeIn>
              <p className="heading-font text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-blue-500 mb-6">
                {t('home.eyebrow')}
              </p>
            </FadeIn>

            <h1 className="heading-font text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tighter leading-[0.95]">
              <StaggerList delay={0.1}>
                <StaggerItem className="block text-white">
                  {t('home.h1_a')}{' '}
                  <span className="text-blue-500 inline-block">{t('home.h1_b')}</span>
                </StaggerItem>
                <StaggerItem className="block text-gradient">{t('home.h1_c')}</StaggerItem>
                <StaggerItem className="block text-white">{t('home.h1_d')}</StaggerItem>
              </StaggerList>
            </h1>

            <FadeIn delay={0.4}>
              <p className="mt-8 text-lg sm:text-xl text-zinc-400 max-w-xl leading-relaxed">
                {t('home.sub')}
              </p>
            </FadeIn>

            <FadeIn delay={0.55}>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-blue-600 hover:bg-blue-500 transition font-semibold text-white group"
                >
                  {t('home.cta_primary')}
                  <ArrowRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isRTL ? 'rtl-flip' : ''}`} />
                </Link>
                <Link
                  to="/portfolio"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-md border border-white/15 hover:border-white/40 hover:bg-white/5 transition font-semibold text-white"
                >
                  {t('home.cta_secondary')}
                </Link>
              </div>
            </FadeIn>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <motion.div
              style={{ y: imgY, scale: imgScale }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-blue-600/20 blur-3xl rounded-full" />
              <motion.img
                src={HERO_IMG}
                alt="3D abstract"
                className="relative w-full rounded-2xl border border-white/10 shadow-2xl"
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className={`absolute -top-6 ${isRTL ? '-right-6' : '-left-6'} w-20 h-20 rounded-full border border-yellow-400/40 grid place-items-center bg-black/50 backdrop-blur`}
              >
                <Sparkles className="w-8 h-8 text-yellow-400" />
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-500 text-xs uppercase tracking-[0.3em]"
        >
          {t('home.scroll')}
          <motion.div animate={{ y: [0, 6, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <ArrowDown className="w-4 h-4" />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="relative py-12 border-y border-white/5 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 heading-font">{t('home.marquee')}</p>
        </div>
        <div className="marquee-mask">
          <Marquee gradient={false} speed={40} pauseOnHover>
            {TECH.concat(TECH).map((tech, i) => (
              <span
                key={i}
                className="heading-font text-3xl md:text-5xl font-black text-zinc-700 hover:text-blue-500 transition-colors mx-8"
              >
                {tech} <span className="text-blue-500/40">·</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <SectionEyebrow>{t('home.stats_title')}</SectionEyebrow>
          <StaggerList className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: 120, suf: '+', label: t('home.stats_projects'), icon: Layers },
              { v: 85, suf: '+', label: t('home.stats_clients'), icon: Globe },
              { v: 8, suf: '', label: t('home.stats_years'), icon: Zap },
              { v: 18, suf: '', label: t('home.stats_team'), icon: Shield },
            ].map((s, i) => (
              <StaggerItem key={i}>
                <div className="tech-card rounded-xl border border-white/10 bg-zinc-950 p-6 md:p-8 h-full hover:border-white/20 transition">
                  <s.icon className="w-6 h-6 text-blue-500 mb-4" />
                  <div className="heading-font text-5xl md:text-6xl font-black text-white">
                    <Counter to={s.v} suffix={s.suf} />
                  </div>
                  <p className="mt-2 text-sm text-zinc-400">{s.label}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerList>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-6 items-end mb-16">
            <div className="md:col-span-7">
              <SectionEyebrow>{t('home.services_eyebrow')}</SectionEyebrow>
              <SectionTitle className="mt-3">{t('home.services_title')}</SectionTitle>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* ERP card */}
            <FadeIn>
              <ServiceCard
                title={t('home.service_erp_title')}
                desc={t('home.service_erp_desc')}
                img={ERP_IMG}
                icon={BookOpen}
                color="from-blue-600/30 to-transparent"
                href="/services"
                cta={t('home.learn_more')}
                isRTL={isRTL}
              />
            </FadeIn>
            <FadeIn delay={0.15}>
              <ServiceCard
                title={t('home.service_web_title')}
                desc={t('home.service_web_desc')}
                img={WEB_IMG}
                icon={Code2}
                color="from-yellow-400/30 to-transparent"
                href="/services"
                cta={t('home.learn_more')}
                isRTL={isRTL}
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 md:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <SectionEyebrow>{t('home.why_eyebrow')}</SectionEyebrow>
            <SectionTitle className="mt-3">{t('home.why_title')}</SectionTitle>
            <FadeIn delay={0.2}>
              <p className="mt-6 text-lg text-zinc-400 leading-relaxed">{t('home.why_desc')}</p>
            </FadeIn>
          </div>
          <div className="md:col-span-7">
            <StaggerList className="grid sm:grid-cols-2 gap-4">
              {['why_1', 'why_2', 'why_3', 'why_4'].map((k, i) => (
                <StaggerItem key={k}>
                  <div className="tech-card rounded-xl border border-white/10 bg-zinc-950 p-6 h-full hover:border-blue-500/40 transition">
                    <div className="heading-font text-5xl font-black text-blue-500 mb-3">0{i + 1}</div>
                    <p className="text-zinc-300 leading-relaxed">{t(`home.${k}`)}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerList>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServiceCard = ({ title, desc, img, icon: Icon, color, href, cta, isRTL }) => (
  <Link
    to={href}
    data-testid={`service-card-${title}`}
    className="tech-card group block relative overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 h-full"
  >
    <div className="aspect-[16/10] overflow-hidden">
      <motion.img
        src={img}
        alt={title}
        whileHover={{ scale: 1.08 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full h-full object-cover"
      />
      <div className={`absolute inset-0 bg-gradient-to-tr ${color} mix-blend-overlay`} />
    </div>
    <div className="p-7">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-white/5 border border-white/10 grid place-items-center">
          <Icon className="w-5 h-5 text-blue-400" />
        </div>
        <h3 className="heading-font text-2xl font-bold text-white">{title}</h3>
      </div>
      <p className="text-zinc-400 leading-relaxed">{desc}</p>
      <div className="mt-6 inline-flex items-center gap-2 text-blue-400 font-semibold text-sm group-hover:gap-3 transition-all">
        {cta}
        <ArrowRight className={`w-4 h-4 ${isRTL ? 'rtl-flip' : ''}`} />
      </div>
    </div>
  </Link>
);

export default HomePage;
